import { useState, useMemo, useRef, useCallback } from 'react';
import './ToolStyles.css';
import { diffLines } from '../utils/diff';
import {
  detectFormat,
  highlightLine,
  FORMAT_LABELS,
  FORMATS,
  type Format,
} from '../utils/syntaxHighlight';
import { formatByType } from '../utils/formatText';

/* ========== 内联样式 ========== */

const DIFF_STYLES = `
.diff-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.diff-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.diff-inputs .input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.diff-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.diff-controls .format-select {
  flex: 0 0 auto;
  min-width: 140px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  user-select: none;
}

.toggle-switch {
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background-color: var(--border-color);
  position: relative;
  transition: background-color 0.2s;
}

.toggle-switch.on {
  background-color: var(--accent);
}

.toggle-switch::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #fff;
  transition: transform 0.2s;
}

.toggle-switch.on::after {
  transform: translateX(18px);
}

.diff-result {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}

.diff-result-header {
  display: flex;
  background-color: var(--bg-surface-2);
  border-bottom: 1px solid var(--border-color);
}

.diff-result-header > div {
  flex: 1;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-align: center;
}

.diff-result-header > div:first-child {
  border-right: 1px solid var(--border-color);
}

.diff-panels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-height: 520px;
  overflow-y: auto;
}

.diff-panel {
  font-family: 'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  line-height: 1.65;
  overflow-x: auto;
}

.diff-panel:first-child {
  border-right: 1px solid var(--border-color);
}

.diff-row {
  display: contents;
}

.diff-cell {
  padding: 1px 8px;
  white-space: pre;
  min-height: 1.65em;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.diff-cell .line-num {
  flex-shrink: 0;
  width: 32px;
  text-align: right;
  color: var(--text-tertiary);
  font-size: 11px;
  user-select: none;
}

.diff-cell .line-content {
  flex: 1;
  min-width: 0;
  word-break: break-all;
}

.diff-cell.added {
  background-color: rgba(52, 211, 153, 0.12);
}
.diff-cell.removed {
  background-color: rgba(248, 113, 113, 0.12);
}
.diff-cell.empty {
  background-color: var(--bg-surface-2);
}

/* 词级差异标记 */
.word-removed {
  background-color: rgba(248, 113, 113, 0.35);
  border-radius: 2px;
}
.word-added {
  background-color: rgba(52, 211, 153, 0.35);
  border-radius: 2px;
}

/* 语法高亮 token 颜色 — 亮色主题 */
.token-keyword { color: #d73a49; }
.token-string { color: #032f62; }
.token-number { color: #005cc5; }
.token-comment { color: #6a737d; font-style: italic; }
.token-tag { color: #22863a; }
.token-attr { color: #6f42c1; }
.token-punct { color: #24292e; }

/* 暗色主题 token 颜色 */
[data-theme="dark"] .token-keyword { color: #ff7b72; }
[data-theme="dark"] .token-string { color: #a5d6ff; }
[data-theme="dark"] .token-number { color: #79c0ff; }
[data-theme="dark"] .token-comment { color: #8b949e; }
[data-theme="dark"] .token-tag { color: #7ee787; }
[data-theme="dark"] .token-attr { color: #d2a8ff; }
[data-theme="dark"] .token-punct { color: #c9d1d9; }

.format-error {
  padding: 6px 12px;
  background-color: var(--danger-soft);
  border-radius: 8px;
  color: var(--danger);
  font-size: 12px;
  margin-top: 4px;
}

@media (max-width: 768px) {
  .diff-inputs {
    grid-template-columns: 1fr;
  }
  .diff-panels {
    grid-template-columns: 1fr;
  }
}
`;

const TextDiff = () => {
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [format, setFormat] = useState<'auto' | Format>('auto');
  const [autoFormat, setAutoFormat] = useState(false);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // 同步滚动
  const handleScroll = useCallback(
    (source: 'left' | 'right') => {
      const sourceRef = source === 'left' ? leftPanelRef : rightPanelRef;
      const targetRef = source === 'left' ? rightPanelRef : leftPanelRef;
      if (sourceRef.current && targetRef.current) {
        targetRef.current.scrollTop = sourceRef.current.scrollTop;
        targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
      }
    },
    []
  );

  // 检测或使用手动选择的格式
  const effectiveFormat = useMemo<Format>(() => {
    if (format !== 'auto') return format;
    const combined = [leftText, rightText].filter(Boolean).join('\n');
    return detectFormat(combined);
  }, [format, leftText, rightText]);

  // 格式化（如果开启自动格式化）
  const leftFormatted = useMemo(() => {
    if (!autoFormat || !leftText) return { formatted: leftText, error: undefined as string | undefined };
    return formatByType(leftText, effectiveFormat);
  }, [autoFormat, leftText, effectiveFormat]);

  const rightFormatted = useMemo(() => {
    if (!autoFormat || !rightText) return { formatted: rightText, error: undefined as string | undefined };
    return formatByType(rightText, effectiveFormat);
  }, [autoFormat, rightText, effectiveFormat]);

  // Diff 计算
  const diffResult = useMemo(() => {
    const left = leftFormatted.formatted;
    const right = rightFormatted.formatted;
    if (!left && !right) return [];
    return diffLines(left, right);
  }, [leftFormatted.formatted, rightFormatted.formatted]);

  // 构建并排行数据
  const sideBySideRows = useMemo(() => {
    const rows: {
      left: { lineNum?: number; content: string; wordDiffs?: typeof diffResult[0]['wordDiffs']; className: string } | null;
      right: { lineNum?: number; content: string; wordDiffs?: typeof diffResult[0]['wordDiffs']; className: string } | null;
      key: number;
    }[] = [];

    diffResult.forEach((line, idx) => {
      if (line.type === 'equal') {
        rows.push({
          left: { lineNum: line.leftLineNumber, content: line.content, className: '' },
          right: { lineNum: line.rightLineNumber, content: line.content, className: '' },
          key: idx,
        });
      } else if (line.type === 'removed') {
        rows.push({
          left: { lineNum: line.leftLineNumber, content: line.content, wordDiffs: line.wordDiffs, className: 'removed' },
          right: null,
          key: idx,
        });
      } else if (line.type === 'added') {
        rows.push({
          left: null,
          right: { lineNum: line.rightLineNumber, content: line.content, wordDiffs: line.wordDiffs, className: 'added' },
          key: idx,
        });
      }
    });

    return rows;
  }, [diffResult]);

  // 渲染行内容（含词级差异标记）
  const renderContent = (
    content: string,
    wordDiffs?: typeof diffResult[0]['wordDiffs']
  ) => {
    if (!wordDiffs || wordDiffs.length === 0) {
      return { __html: highlightLine(effectiveFormat, content) };
    }
    // 有词级差异：交替使用语法高亮和差异标记
    let html = '';
    for (const seg of wordDiffs) {
      const highlighted = highlightLine(effectiveFormat, seg.text);
      if (seg.type === 'removed') {
        html += `<span class="word-removed">${highlighted}</span>`;
      } else if (seg.type === 'added') {
        html += `<span class="word-added">${highlighted}</span>`;
      } else {
        html += highlighted;
      }
    }
    return { __html: html };
  };

  const handleClear = () => {
    setLeftText('');
    setRightText('');
  };

  const handleSwap = () => {
    setLeftText(rightText);
    setRightText(leftText);
  };

  return (
    <div className="tool-container">
      {/* 注入组件专属样式 */}
      <style>{DIFF_STYLES}</style>

      {/* 控制栏 */}
      <div className="diff-controls">
        <select
          className="tool-select format-select"
          value={format}
          onChange={(e) => setFormat(e.target.value as 'auto' | Format)}
        >
          <option value="auto">自动检测</option>
          {FORMATS.map((f) => (
            <option key={f} value={f}>
              {FORMAT_LABELS[f]}
            </option>
          ))}
        </select>

        <div className="toggle-row" onClick={() => setAutoFormat(!autoFormat)}>
          <div className={`toggle-switch${autoFormat ? ' on' : ''}`} />
          <span>自动格式化</span>
        </div>

        {format === 'auto' && effectiveFormat !== 'plain' && (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            检测: {FORMAT_LABELS[effectiveFormat]}
          </span>
        )}
      </div>

      {/* 输入区 */}
      <div className="diff-inputs">
        <div className="input-group">
          <label className="input-label">原始文本</label>
          <textarea
            className="tool-textarea"
            value={leftText}
            onChange={(e) => setLeftText(e.target.value)}
            rows={8}
            placeholder="输入原始文本..."
          />
          {leftFormatted.error && (
            <div className="format-error">{leftFormatted.error}</div>
          )}
        </div>
        <div className="input-group">
          <label className="input-label">修改后文本</label>
          <textarea
            className="tool-textarea"
            value={rightText}
            onChange={(e) => setRightText(e.target.value)}
            rows={8}
            placeholder="输入修改后文本..."
          />
          {rightFormatted.error && (
            <div className="format-error">{rightFormatted.error}</div>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="button-group">
        <button className="tool-button" onClick={handleClear}>
          清空
        </button>
        <button className="tool-button" onClick={handleSwap}>
          交换左右
        </button>
      </div>

      {/* Diff 结果 */}
      {diffResult.length > 0 && (
        <div className="diff-result">
          <div className="diff-result-header">
            <div>原始文本</div>
            <div>修改后文本</div>
          </div>
          <div className="diff-panels">
            <div
              className="diff-panel"
              ref={leftPanelRef}
              onScroll={() => handleScroll('left')}
            >
              {sideBySideRows.map((row) => (
                <div key={row.key} className="diff-cell-wrapper">
                  {row.left ? (
                    <div className={`diff-cell ${row.left.className}`}>
                      <span className="line-num">{row.left.lineNum ?? ''}</span>
                      <span
                        className="line-content"
                        dangerouslySetInnerHTML={renderContent(
                          row.left.content,
                          row.left.wordDiffs
                        )}
                      />
                    </div>
                  ) : (
                    <div className="diff-cell empty">
                      <span className="line-num" />
                      <span className="line-content" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div
              className="diff-panel"
              ref={rightPanelRef}
              onScroll={() => handleScroll('right')}
            >
              {sideBySideRows.map((row) => (
                <div key={row.key} className="diff-cell-wrapper">
                  {row.right ? (
                    <div className={`diff-cell ${row.right.className}`}>
                      <span className="line-num">{row.right.lineNum ?? ''}</span>
                      <span
                        className="line-content"
                        dangerouslySetInnerHTML={renderContent(
                          row.right.content,
                          row.right.wordDiffs
                        )}
                      />
                    </div>
                  ) : (
                    <div className="diff-cell empty">
                      <span className="line-num" />
                      <span className="line-content" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {diffResult.length === 0 && (leftText || rightText) && (
        <div className="info-text">输入两侧文本后显示对比结果</div>
      )}
    </div>
  );
};

export default TextDiff;
